// Catches a reading typed or read into the wrong parameter, the kind no
// test kit could produce. The case that shipped: a refractometer's 1.023
// (salinity as specific gravity) saved as alkalinity, which then paged the
// keeper at severe, told them to dose, and had the dose calculator work
// out a full day's alkalinity dose against it.
//
// Narrow on purpose: only values that cannot be a real reading of that
// parameter, never values that are merely unusual (the outlier check is
// for those). Storage units: alk dKH, calcium and magnesium ppm, temp °F.
// Pure, shared by the server and /app.

import { FRESHWATER_SUBTYPES } from './param-explainers.js';
import { PARAM_LABEL_LOWER } from './param-labels.js';

// 1.005: a refractometer never reads a saltwater tank lower, and a real
// alk crash to 1.0 dKH must still be loggable.
const SG_LOW = 1.005;
const SG_HIGH = 1.04;

// Where a specific-gravity number cannot be real. Alkalinity, calcium and
// magnesium only on saltwater tanks: soft fresh water can read 1 dKH or
// 1 ppm calcium for real.
const SG_IMPOSSIBLE_ANYWHERE = new Set(['temp', 'ph']);
const SG_IMPOSSIBLE_IN_SALTWATER = new Set(['alk', 'calcium', 'magnesium']);

function isSaltwater(subtypeSlug) {
  return !FRESHWATER_SUBTYPES.includes(String(subtypeSlug || ''));
}

// null when the value could be a real reading of `param`; otherwise
// { code, likely, message } with plain words for the keeper. `shown` is
// what the keeper typed, in their own unit ("30°C", "1.25"), for the
// message; the check itself always runs on the storage value.
export function implausibleReading(
  param,
  value,
  { subtypeSlug = null, shown = null, automated = false } = {}
) {
  const out = implausibleCore(param, value, subtypeSlug, shown);
  // From a probe or controller, deleting the row fixes nothing (the next
  // one is the same) and it cannot be a typo: say what can cause it.
  if (out && automated) {
    const name = PARAM_LABEL_LOWER[param] || param;
    out.stored = `Your controller sent ${Number(value)} for ${name}, which is not a real reading. Common causes: a probe out of the water or not fitted, or the controller set to other units. It is left out of your score and alerts until a real reading arrives.`;
  }
  return out;
}

function implausibleCore(param, value, subtypeSlug, shown) {
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  if (param === 'salinity') return implausibleSalinity(v, subtypeSlug, shown);
  // Storage is °F. Below 40 or above 110 is no aquarium's water: usually a
  // °C keeper's number read as °F, or a value on the wrong card.
  if (param === 'temp' && (v < 40 || v > 110)) {
    return {
      code: 'temp_out_of_range',
      likely: null,
      message: `${shown ?? `${Math.round(v * 10) / 10}°F`} is not a tank temperature. Check the number, and whether it is in °F or °C.`,
      stored: `${Math.round(v * 10) / 10}°F is not a tank temperature, so it is left out of your score and alerts. Delete it in Temperature history and log the reading again.`,
    };
  }
  // An S.G.-shaped number first: its message offers to log it as salinity.
  const sgShaped = v >= SG_LOW && v <= SG_HIGH;
  const impossible =
    sgShaped &&
    (SG_IMPOSSIBLE_ANYWHERE.has(param) ||
      (SG_IMPOSSIBLE_IN_SALTWATER.has(param) && isSaltwater(subtypeSlug)));
  if (!impossible) return implausibleNumber(param, v, subtypeSlug, shown);
  const name = PARAM_LABEL_LOWER[param] || param;
  return {
    code: 'looks_like_salinity',
    likely: 'salinity',
    message: `${v} looks like a salinity reading (specific gravity), not ${name}. Save it as salinity instead, or check the number.`,
    stored: `${v} looks like a salinity number, not ${name}, so it is left out of your score, alerts and doses. Delete it in ${name} history, and log it as salinity if that is what you tested.`,
  };
}

// Salinity is stored in ppt. On a saltwater tank anything under 5 ppt or
// over 55 is no reading at all: most often a refractometer's S.G. saved
// as ppt (1.025 ppt), an S.G. typo (1.25) that converted to hundreds, or
// a conductivity probe out of the water reporting 0 (device ingest drops
// those rather than alert on them).
// Hyposalinity in a QT sits well above 5. Fresh water is left alone: a
// little salt in a livebearer or brackish tank is real.
const SALINITY_PPT_LOW = 5;
const SALINITY_PPT_HIGH = 55;

function implausibleSalinity(v, subtypeSlug, shown) {
  // No water reads below zero, fresh water included (a freshwater
  // salinity of -1 was saved; nightly sweep 3, 2026-10-25).
  if (v < 0) {
    return {
      code: 'negative',
      likely: null,
      message: `${shown ?? v} is not a possible salinity reading. Check the number.`,
      stored: `${v} is not a possible salinity reading, so it is left out of your score and alerts. Delete it in Salinity history and log the reading again.`,
    };
  }
  if (!isSaltwater(subtypeSlug)) return null;
  if (v >= SALINITY_PPT_LOW && v <= SALINITY_PPT_HIGH) return null;
  return {
    code: 'salinity_out_of_range',
    likely: null,
    message: `${shown ?? v} is not a saltwater salinity reading. Check the number and its unit: S.G. looks like 1.025, ppt like 35.`,
    stored: `${v} ppt is not a saltwater salinity reading, so it is left out of your score and alerts. It may be a specific gravity saved as ppt, or a probe reading out of the water. Delete it in Salinity history and log the reading again.`,
  };
}

// Numbers no test kit or probe can give for the param at all, found when
// the server saved nitrate -5, pH 70, calcium 0 on a reef and phosphate
// 9999, and queued severe alerts for each (audit 2026-10-01). A typo or a
// probe out of the water, never a reading.
const NEVER_NEGATIVE = new Set([
  'alk',
  'calcium',
  'magnesium',
  'nitrate',
  'phosphate',
  'ammonia',
  'nitrite',
  'kh',
  'gh',
  'tds',
  'potassium',
]);
// pH below 3 or above 11 is no aquarium's water; a pH probe reads 0 or 14
// when it is dry or broken.
const PH_LOW = 3;
const PH_HIGH = 11;
// Saltwater carries calcium and magnesium in the salt itself, so a reading
// under this is a wrong card or a typo, even in a hyposalinity QT.
const SALTWATER_MIN_PPM = 10;
const PHOSPHATE_MAX_PPM = 100;

function implausibleNumber(param, v, subtypeSlug, shown) {
  const name = PARAM_LABEL_LOWER[param] || param;
  const typed = shown ?? v;
  const refuse = (code) => ({
    code,
    likely: null,
    message: `${typed} is not a possible ${name} reading. Check the number, and that it is on the right card.`,
    stored: `${v} is not a possible ${name} reading, so it is left out of your score and alerts. Delete it in ${name} history and log the reading again.`,
  });
  if (NEVER_NEGATIVE.has(param) && v < 0) return refuse('negative');
  if (param === 'ph' && (v < PH_LOW || v > PH_HIGH)) return refuse('ph_out_of_range');
  if (
    (param === 'calcium' || param === 'magnesium') &&
    isSaltwater(subtypeSlug) &&
    v >= 0 &&
    v < SALTWATER_MIN_PPM
  ) {
    return refuse('saltwater_too_low');
  }
  if (param === 'phosphate' && v > PHOSPHATE_MAX_PPM) return refuse('phosphate_out_of_range');
  return null;
}
